import { Component, type ErrorInfo, type ReactNode } from 'react'
import { apiClient } from '../api/client'

/**
 * Garde-fou autour de l'application.
 *
 * Sans lui, la moindre erreur de rendu vide la page : le visiteur voit du
 * BLANC, sans message et sans trace — arrivé le 24/10/2026 juste après l'envoi
 * d'une demande d'enlèvement (la demande était pourtant bien enregistrée, et
 * l'e-mail parti). Le journal du serveur ne dit rien de ce qui se passe dans
 * le navigateur ; c'est donc au navigateur de le raconter.
 *
 * Deux rôles, dans cet ordre d'importance :
 *  1. dire au visiteur que sa demande est peut-être partie, et lui donner de
 *     quoi s'en assurer (le téléphone du magasin) plutôt qu'une page morte ;
 *  2. envoyer l'erreur au serveur pour qu'elle soit diagnosticable.
 */
export class GardeFou extends Component<{ children: ReactNode }, { plante: boolean }> {
  state = { plante: false }

  static getDerivedStateFromError() {
    return { plante: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // L'envoi ne doit jamais faire échouer l'affichage de secours : on ignore
    // son issue. `catch` vide volontaire.
    try {
      apiClient.post('/api/client-error', {
        message: String(error?.message ?? error).slice(0, 500),
        stack: String(error?.stack ?? '').slice(0, 4000),
        composants: String(info?.componentStack ?? '').slice(0, 2000),
        url: window.location.href.slice(0, 500),
        navigateur: navigator.userAgent.slice(0, 300),
      }).catch(() => {})
    } catch {
      /* rien : le garde-fou ne peut pas se permettre de planter à son tour */
    }
  }

  render() {
    if (!this.state.plante) return this.props.children
    return (
      <div className="mx-auto max-w-xl px-5 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-navy">Un problème d'affichage</h1>
        <p className="mt-4 text-ink/75">
          La page n'a pas pu s'afficher. <strong>Si vous veniez d'envoyer une demande,
          elle a probablement bien été enregistrée</strong> — nous vous recontacterons.
        </p>
        <p className="mt-3 text-ink/75">
          En cas de doute, appelez-nous au <a href="tel:081301194" className="font-semibold text-navy underline">081 30 11 94</a>.
        </p>
        <button
          onClick={() => window.location.assign('/')}
          className="mt-6 rounded-full bg-navy px-5 py-2 text-sm font-semibold text-white hover:bg-navy-dk"
        >
          Retour à l'accueil
        </button>
      </div>
    )
  }
}
